import StateStepper from '@/components/StateStepper/StateStepper';
import type {
  OrderAppDeliveryMode,
  OrderAppOrderStatus,
} from '@/graphql/orderApp/api';
import * as OM from '@/messages/order';

import * as S from './OrderStatusStepper.styles';

export interface OrderStatusStepperProps {
  deliveryMode?: OrderAppDeliveryMode;
  status: OrderAppOrderStatus;
}

export const ORDER_STATUS_MESSAGE_MAPPING: Partial<
  Record<OrderAppOrderStatus, JSX.Element>
> = {
  CANCELLED: OM.CanceledOrderStatus,
  COLLECTED: OM.CollectedOrderStatus,
  CONFIRMED: OM.ConfirmedOrderStatus,
  DRAFT: OM.DraftOrderStatus,
  IN_PREPARATION: OM.InPreparationOrderStatus,
  IN_VALIDATION: OM.InValidationOrderStatus,
  PARTIALLY_PAID: OM.PartiallyPaidOrderStatus, // Multicard payment partially paid
  PARTIALLY_RETURNED: OM.PartiallyReturnedOrderStatus,
  PARTIAL_PAYMENT_IN_VALIDATION: OM.PartialPaymentInValidationOrderStatus,
  PARTIAL_PAYMENT_RECEIVED: OM.PartialPaymentReceivedOrderStatus, // Preorder partially paid
  PARTIAL_PAYMENT_REJECTED: OM.PartialPaymentRejectedOrderStatus,
  PENDING: OM.PendingPaymentOrderStatus,
  PREORDER_CANCELLED: OM.PreorderCancelled,
  READY_FOR_COLLECTION: OM.ReadyForCollectionOrderStatusDescription,
  READY_TO_SHIP: OM.ReadyToShipOrderStatus,
  REJECTED: OM.RejectedOrderStatus,
  RETURNED: OM.ReturnedOrderStatus,
  RETURN_ACCEPTED: OM.ReturnAcceptedOrderStatus,
  RETURN_RECEIVED: OM.ReturnReceivedOrderStatus,
  RETURN_REGISTERED: OM.ReturnRegisteredOrderStatus,
  RETURN_REJECTED: OM.ReturnRejectedOrderStatus,
  SHIPPED: OM.ShippedOrderStatus,
};

const HOME_DELIVERY_STATUSES: OrderAppOrderStatus[] = [
  'IN_VALIDATION',
  'CONFIRMED',
  'IN_PREPARATION',
  'READY_TO_SHIP',
  'SHIPPED',
];

const BOUTIQUE_PICKUP_STATUSES: OrderAppOrderStatus[] = [
  'IN_VALIDATION',
  'CONFIRMED',
  'IN_PREPARATION',
  'READY_TO_SHIP',
  'SHIPPED',
  'READY_FOR_COLLECTION',
  'COLLECTED',
];

const getActiveStep = (
  status: OrderAppOrderStatus,
  statuses: OrderAppOrderStatus[],
): number => {
  return statuses.indexOf(status) + 1;
};

export const OrderStatusStepper = ({
  deliveryMode = 'HOME_DELIVERY',
  status,
}: OrderStatusStepperProps) => {
  let statuses: OrderAppOrderStatus[] = [];

  if (deliveryMode === 'HOME_DELIVERY') {
    statuses = HOME_DELIVERY_STATUSES;
  }

  if (deliveryMode === 'BOUTIQUE_PICKUP') {
    statuses = BOUTIQUE_PICKUP_STATUSES;
  }

  return (
    <S.OrderStatusStepperWrapper>
      <StateStepper
        activeStep={getActiveStep(status, statuses)}
        data-test-id='OrderStatusStepper'
        isFullWidth
      >
        {statuses?.map((status, i) => (
          <StateStepper.Step
            key={status + i}
            data-test-id={`OrderStatusStepperStep-${i}`}
          >
            <StateStepper.Label data-test-id={`OrderStatusStepperLabel-${i}`}>
              {ORDER_STATUS_MESSAGE_MAPPING[status]}
            </StateStepper.Label>
          </StateStepper.Step>
        ))}
      </StateStepper>
    </S.OrderStatusStepperWrapper>
  );
};

OrderStatusStepper.displayName = 'OrderStatusStepper';
